import React, { useState, useEffect } from "react";
import { useStateValue } from "../../State";
import "./combinedplan.css";

import ContentBoxMUI from "../../Components/ContentBoxMUI";
import Languages from "../../translations";
import InfoModal from "../../Components/Modals/InfoModal";
import SnackbarComponent from "../../Components/Snackbar";

import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import BlockIcon from "@mui/icons-material/Block";
import Typography from "@mui/material/Typography";
import LockIcon from "@mui/icons-material/Lock";

import { types } from "../../Reducers/actionTypes";
import { getProfilePictureData } from "../../utils";
import { getCombinedPlanSectionTitle } from "./sectionTitles";

import ChildRow from "../Vasu/ChildRow";
import GeneralInfo from "./GeneralInfo";

const renderMenuItemText = (
  title,
  subtitle,
  lastModified,
  showLastModified = true,
) => (
  <ListItemText
    primaryTypographyProps={{
      fontWeight: "Bold",
      fontSize: 16,
      ml: 2,
      mb: 1,
    }}
    primary={title}
    secondary={
      <React.Fragment>
        <ListItemText
          primaryTypographyProps={{
            fontSize: 15,
            mb: 1,
            ml: 2,
          }}
          primary={subtitle}
          secondaryTypographyProps={{
            fontSize: 14,
            fontStyle: "italic",
            ml: 2,
          }}
          secondary={
            showLastModified
              ? Languages.generalLastModified + ": " + (lastModified || "")
              : null
          }
        />
      </React.Fragment>
    }
  />
);

const CombinedPlanContainer = (props) => {
  const { state, actions } = useStateValue();
  const { history } = props;
  const { children } = state;
  const combinedPlanState = state.combinedPlan;
  const data = combinedPlanState.data;
  const childId = props.match.params.ChildId;

  // Derive child info reactively so it updates when children list loads after a page refresh
  const child = children.ChildList.find((c) => c.Id === childId);
  const profilePicBase64 = getProfilePictureData(state, child?.Id);
  const childColor = child?.BgColor ?? null;
  const childFirstName = child?.FirstName ?? null;
  const childLastName = child?.LastName ?? null;
  const CombinedPlanId = child?.CombinedPlanId ?? null;
  const childrenLoaded = children.state === "LOAD_SUCCESS_CHILDREN";

  const [LoadingData, setLoadingData] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [showSnackbar, setshowSnackbar] = useState(false);
  const [NoteText, setNoteText] = useState(null);

  // Re-run when CombinedPlanId becomes available (e.g. after page refresh when children load)
  useEffect(() => {
    if (
      combinedPlanState.state === "INITIAL_STATE" &&
      CombinedPlanId &&
      !LoadingData
    ) {
      actions.triggerLoadCombinedPlan(CombinedPlanId);
      setLoadingData(true);
    }
  }, [CombinedPlanId]);

  useEffect(() => {
    switch (combinedPlanState.state) {
      case types.LOADING_COMBINED_PLAN:
        break;
      case types.COMBINED_PLAN_LOAD_OK:
        setLoadingData(false);
        break;
      case types.COMBINED_PLAN_LOAD_FAILED:
        setLoadingData(false);
        setshowSnackbar(true);
        setNoteText(
          combinedPlanState.Code === 404
            ? Languages.errorNotFound
            : combinedPlanState.Code === 500
              ? Languages.errorBackend
              : Languages.requestFailed,
        );
        break;
      default:
        break;
    }
  }, [combinedPlanState]);

  useEffect(() => {
    if (!data?.Locked || !childId) {
      setShowNote(false);
      return;
    }

    const noteKey = `showCombinedPlanNote_${childId}`;
    const storedValue = sessionStorage.getItem(noteKey);

    if (storedValue !== null) {
      setShowNote(JSON.parse(storedValue));
      return;
    }

    setShowNote(true);
    sessionStorage.setItem(noteKey, JSON.stringify(true));
  }, [data?.Locked, childId]);

  const handleSnackbarCB = () => {
    history.goBack();
  };

  const closeNote = () => {
    setShowNote(false);
    if (childId) {
      sessionStorage.setItem(
        `showCombinedPlanNote_${childId}`,
        JSON.stringify(false),
      );
    }
  };

  const handleBoxClick = (section) => {
    const childId = props.match.params.ChildId;
    switch (section) {
      case "learningAreas":
        history.push(`/combinedplan/${childId}/learningareas`);
        break;
      case "description":
        history.push(`/combinedplan/${childId}/description`);
        break;
      case "springEvaluation":
        history.push(`/combinedplan/${childId}/springevaluation`);
        break;
      case "discussion":
        history.push(`/combinedplan/${childId}/discussion`);
        break;
      case "support":
        history.push(`/combinedplan/${childId}/support`);
        break;
      default:
        break;
    }
  };

  const hasLearningAreas =
    data && data.LearningAreas && data.LearningAreas.length > 0;
  const hasDescription =
    data &&
    data.DescriptionOfChildRows &&
    data.DescriptionOfChildRows.length > 0;
  const hasSpringEvaluation =
    data &&
    data.IsLeopsActive &&
    data.SpringEvaluation &&
    (data.SpringEvaluation.IndependentSection ||
      data.SpringEvaluation.TeacherSummarySection);
  const hasDiscussion =
    data &&
    ((data.DiscussionPrevVasuNotes?.Title || "") !== "" ||
      (data.DiscussionPrevVasuNotes?.SubTitle || "") !== "" ||
      (data.DiscussionPrevVasuNotes?.Text || "") !== "" ||
      (data.DiscussionObjectives?.EvaluationSections &&
        data.DiscussionObjectives.EvaluationSections.length > 0) ||
      (data.DiscussionOtherItems?.EvaluationSections &&
        data.DiscussionOtherItems.EvaluationSections.length > 0));
  const hasSupport = data && data.SupportTiers != null;
  //Tehty leops jos lapsilistassa leopsid mukana
  const hasLeopsSupportPlan = child && child.LeopsId != null;

  const supportHasActiveTier =
    data &&
    (data.SupportTiers?.GeneralSupport?.StartDate != null ||
      data.SupportTiers?.EnhancedSupport?.StartDate != null ||
      data.SupportTiers?.SpecialSupport?.StartDate != null);
  const sectionOptions = { hasLeopsSupportPlan: !!hasLeopsSupportPlan };
  const learningAreasTitle = getCombinedPlanSectionTitle(
    data,
    "learningAreas",
    data?.LearningAreasSectionTitle,
    "",
    sectionOptions,
  );
  const discussionTitle = getCombinedPlanSectionTitle(
    data,
    "discussion",
    data?.DiscussionSectionTitle,
    "",
    sectionOptions,
  );
  const supportTitle = getCombinedPlanSectionTitle(
    data,
    "support",
    data?.SupportSectionTitle,
    "",
    sectionOptions,
  );
  const springEvaluationTitle = getCombinedPlanSectionTitle(
    data,
    "springEvaluation",
    data?.SpringEvaluationSectionTitle,
    "Kevään arviointi",
    sectionOptions,
  );
  const descriptionTitle = getCombinedPlanSectionTitle(
    data,
    "description",
    data?.DescriptionOfChildSectionTitle,
    "",
    sectionOptions,
  );

  if (
    combinedPlanState.state === "INITIAL_STATE" &&
    !CombinedPlanId &&
    childrenLoaded
  ) {
    return (
      <div className="CombinedPlanContainer">
        <ContentBoxMUI>
          <Typography sx={{ p: 2 }}>
            {Languages.errorNotFound || "Ei löytynyt"}
          </Typography>
        </ContentBoxMUI>
      </div>
    );
  }

  if (showSnackbar) {
    return (
      <div className="CombinedPlanContainer">
        <ContentBoxMUI>
          <ChildRow
            childpicture={profilePicBase64}
            childFirstName={childFirstName}
            childLastName={childLastName}
            childColor={childColor}
            FormId={CombinedPlanId}
            history={history}
          />
          <SnackbarComponent
            verticalPosition={"bottom"}
            show={showSnackbar}
            message={NoteText}
            callback={() => handleSnackbarCB()}
          />
        </ContentBoxMUI>
      </div>
    );
  }

  return (
    <ContentBoxMUI>
      <div className="CombinedPlanContainer">
        {LoadingData ? (
          <Backdrop open={true}>
            <CircularProgress />
          </Backdrop>
        ) : (
          <div>
            <Box sx={{ mt: 1 }}>
              <ChildRow
                childpicture={profilePicBase64}
                childFirstName={childFirstName}
                childLastName={childLastName}
                childColor={childColor}
                FormId={CombinedPlanId}
                history={history}
              />
            </Box>

            {data && (
              <div>
                {data.Locked && (
                  <InfoModal
                    modalOpen={showNote}
                    headerText={Languages.logged}
                    onClose={closeNote}
                    allowClose={true}
                  >
                    <Typography>{Languages.CombinedPlanLocked}</Typography>
                  </InfoModal>
                )}

                {/* Basic Info & Group VASU */}
                <div className="col-12">
                  <GeneralInfo data={data} />
                </div>

                {/* Conditional Menu Items */}
                <List className="col-12" sx={{ mt: 2 }}>
                  {hasLearningAreas && (
                    <ListItemButton
                      className="boxed"
                      sx={{
                        backgroundColor: "#a6dee0",
                        borderRadius: 0.5,
                        mt: 1.5,
                        boxShadow: 2,
                      }}
                      onClick={() => handleBoxClick("learningAreas")}
                    >
                      {renderMenuItemText(
                        learningAreasTitle,
                        Languages.eduPlanSubtitle2,
                        data.LearningAreasModificationDate,
                      )}
                      <ListItemIcon>
                        <ArrowForwardIosIcon
                          fontWeight="Bold"
                          fontSize="small"
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  )}

                  {hasDiscussion && (
                    <ListItemButton
                      className="boxed"
                      sx={{
                        backgroundColor: "#afdfc8",
                        borderRadius: 0.5,
                        mt: 1.5,
                        boxShadow: 2,
                      }}
                      onClick={() => handleBoxClick("discussion")}
                    >
                      {renderMenuItemText(
                        discussionTitle,
                        Languages.eduPlanSubtitle1,
                        data.DiscussionModificationDate,
                      )}
                      <ListItemIcon>
                        <ArrowForwardIosIcon
                          fontWeight="Bold"
                          fontSize="small"
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  )}

                  {(supportHasActiveTier || hasLeopsSupportPlan) && (
                    <ListItemButton
                      className="boxed"
                      sx={{
                        backgroundColor: "#fceebf",
                        borderRadius: 0.5,
                        mt: 1.5,
                        boxShadow: 2,
                      }}
                      onClick={() => handleBoxClick("support")}
                    >
                      {renderMenuItemText(
                        supportTitle,
                        Languages.eduPlanSubtitle1,
                        data.SupportModificationDate,
                      )}
                      <ListItemIcon>
                        <ArrowForwardIosIcon
                          fontWeight="Bold"
                          fontSize="small"
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  )}

                  {hasSpringEvaluation && (
                    <ListItemButton
                      className="boxed"
                      sx={{
                        backgroundColor: "#d4edda",
                        borderRadius: 0.5,
                        mt: 1.5,
                        boxShadow: 2,
                      }}
                      onClick={() => handleBoxClick("springEvaluation")}
                    >
                      {renderMenuItemText(
                        springEvaluationTitle,
                        Languages.eduPlanSubtitle2,
                        data.SpringEvaluationModificationDate,
                      )}
                      <ListItemIcon>
                        <ArrowForwardIosIcon
                          fontWeight="Bold"
                          fontSize="small"
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  )}

                  {hasDescription && (
                    <ListItemButton
                      className="boxed"
                      sx={{
                        backgroundColor: "#fbcece",
                        borderRadius: 0.5,
                        mt: 1.5,
                        boxShadow: 2,
                      }}
                      onClick={() => handleBoxClick("description")}
                    >
                      {renderMenuItemText(
                        descriptionTitle,
                        Languages.eduPlanSubtitle1,
                        data.DescriptionModificationDate,
                      )}
                      <ListItemIcon>
                        <ArrowForwardIosIcon
                          fontWeight="Bold"
                          fontSize="small"
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  )}
                </List>

                {data.Locked && (
                  <div
                    className="fixed-bottom"
                    style={{
                      left: "auto",
                      fontWeight: "bold",
                      color: "#D500F9",
                    }}
                  >
                    <div className="float-right">
                      {Languages.logged}
                      <LockIcon
                        style={{ color: "#D500F9" }}
                        fontSize="large"
                      ></LockIcon>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </ContentBoxMUI>
  );
};

export default CombinedPlanContainer;
